import { useCallback, useEffect, useRef } from 'react';
import { getFile } from '../services/storage';
import { cleanHtmlForPublish } from '../services/editor';
import removeUnwantedNodes from '../utils/removeUnwantedNode';
import { isHtmlFile, normalizeUrl } from '../utils/path';
import { useToast } from '../contexts/ToastContext';

// Long enough to swallow a burst of typing, short enough that a pause saves.
const SAVE_DEBOUNCE_MS = 400;

export function useEditorCallbacks({
  iframeRef,
  currentFile,
  selectedLink,
  saveFileContent,
  markChanged,
  createNewFile,
  switchFile
}) {
  const { showToast } = useToast();

  // Handle content changes (save to IndexedDB)
  const handleChanges = useCallback(async () => {
    if (!iframeRef.current || !currentFile) return;
    // The iframe only ever shows an HTML file, but selecting a stylesheet or a
    // script in the file list makes it the current file while leaving the
    // preview where it was. Serialising the iframe then writes a whole HTML
    // document over that stylesheet - and publishes it. Nothing stopped this
    // except removeUnwantedNodes happening to throw on a file with no <body>.
    if (!isHtmlFile(currentFile)) return;

    const iframeDoc = iframeRef.current.contentWindow.document;
    const originalHtml = await getFile(`${currentFile}-original`);
    removeUnwantedNodes(originalHtml, iframeDoc);

    let html = iframeDoc.documentElement.outerHTML;
    html = cleanHtmlForPublish(html);

    await saveFileContent(currentFile, html);
    markChanged();
  }, [iframeRef, currentFile, saveFileContent, markChanged]);

  // Typing in the preview fired the save on every keyup, and each run reparses
  // the whole document twice and rewrites it to storage - enough to lock the tab
  // on a large page. Coalesce the burst. Discrete actions (formatting, links,
  // crop) keep calling handleChanges directly and still save at once.
  const saveTimerRef = useRef(null);
  const inFlightRef = useRef(null);

  const handleChangesDebounced = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      inFlightRef.current = handleChanges().finally(() => {
        inFlightRef.current = null;
      });
    }, SAVE_DEBOUNCE_MS);
  }, [handleChanges]);

  // Anything that reads the saved file runs this first, or it reads a version up
  // to one debounce window out of date.
  //
  // It drains what is actually outstanding rather than always re-serialising.
  // An unconditional pass would overwrite the stored file with the iframe on
  // every publish - and the code editor only syncs <body> into the iframe, so
  // edits to <head> (title, meta, scripts) live in storage and not in the DOM.
  // Re-deriving would have silently dropped them from what ships.
  const flushChanges = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      await handleChanges();
      return;
    }
    await inFlightRef.current;
  }, [handleChanges]);

  // Drop a pending save when the file underneath it changes, and on unmount. The
  // queued callback closes over the file it was scheduled for while the iframe
  // has already moved on, so letting it fire writes one file's DOM into another
  // file's record.
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, [currentFile]);

  // Apply text formatting command
  const applyCommand = useCallback((command) => {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow.document.execCommand(command, false, null);
    handleChanges();
  }, [iframeRef, handleChanges]);

  // Handle link creation
  const handleLinkClick = useCallback(() => {
    const url = prompt("Enter the URL:");
    if (url) {
      iframeRef.current.contentWindow.document.execCommand("createLink", false, normalizeUrl(url));
      handleChanges();
    }
  }, [iframeRef, handleChanges]);

  // Handle iframe link editing
  const handleIframeLinkClick = useCallback(() => {
    if (!selectedLink) return;
    const newURL = prompt("Edit link URL:", selectedLink.href);
    if (newURL !== null) {
      selectedLink.href = normalizeUrl(newURL);
      handleChanges();
    }
  }, [selectedLink, handleChanges]);

  // Remove link
  const removeLink = useCallback(() => {
    if (!selectedLink) return;
    selectedLink.removeAttribute("href");
    handleChanges();
  }, [selectedLink, handleChanges]);

  // Handle file creation
  const handleCreateFile = useCallback(async (filePath, content = '') => {
    try {
      const normalized = await createNewFile(filePath, content);
      if (isHtmlFile(normalized)) {
        await switchFile(normalized);
      }
      markChanged();
      showToast(`Created ${normalized}`, 'success');
      return true;
    } catch (err) {
      showToast(err.message, 'error');
      return false;
    }
  }, [createNewFile, switchFile, markChanged, showToast]);

  return {
    handleChanges,
    handleChangesDebounced,
    flushChanges,
    applyCommand,
    handleLinkClick,
    handleIframeLinkClick,
    removeLink,
    handleCreateFile
  };
}
